import { Colors } from '@futrli/colors';
import { CSSProperties } from '@mui/styles';
import feather from 'feather-icons';
import { get, kebabCase } from 'lodash';
import React, { DOMAttributes, FC, SVGAttributes } from 'react';
import InlineSVG from 'svg-inline-react';
import { AnyObject } from '../../core/types';

export interface FeatherIconProps {
  [key: string]: string;
}

export interface FeatherIconStyles {
  default: CSSProperties;
  dark: CSSProperties;
  white: CSSProperties;
}

export type FeatherIconTypes = 'default' | 'white' | 'dark';

interface UIFeatherIconOwnProps {
  /**
   * Name of the icon.
   */
  iconName: string;
  /**
   * Properties to override the styles.
   */
  svgAttributes?: Omit<SVGAttributes<AnyObject>, keyof DOMAttributes<AnyObject>>;
  /**
   * Type of the feather icon to be rendered.
   */
  customType?: FeatherIconTypes;
}

export type UIFeatherIconProps = UIFeatherIconOwnProps & DOMAttributes<AnyObject>;

/**
 * Gets the styles already whitelabelled.
 * @function
 * @name getUIFeatherIconStyles
 * @returns {FeatherIcon} - FeatherIcon with styles
 */
const getUIFeatherIconDefaultStyles = (): FeatherIconStyles => ({
  default: {
    'width': '20px',
    'height': '20px',
    'stroke-width': '1.5px'
  },
  dark: {
    'color': Colors.tealGrey.base,
    'width': '20px',
    'height': '20px',
    'stroke-width': '1.5px'
  },
  white: {
    'color': Colors.white,
    'width': '20px',
    'height': '20px',
    'stroke-width': '2px'
  }
});

/**
 * Renders the feather icons with styles.
 */
export const UIFeatherIcon: FC<UIFeatherIconProps> = props => {
  const { iconName, svgAttributes = {}, customType = 'default' } = props;

  /**
   * Gets the icon from feather and applies the provided attributes.
   *
   * @returns {string} - svg icon as string.
   */
  const featherIcon = (iconName: string) => feather.icons[iconName].toSvg(getSvgAttrs());

  /**
   * Gets the custom attributes passed as props modifying the default ones.
   *
   * @returns {FeatherIconProps} - Properties for icons.
   */
  const getSvgAttrs = (): FeatherIconProps => {
    const featherIconProps: FeatherIconStyles = getUIFeatherIconDefaultStyles();
    const defaultProps = featherIconProps[customType];

    return Object.keys(svgAttributes).reduce((result: FeatherIconProps, value: string) => {
      const svgProp = get(svgAttributes, [value]);

      result[kebabCase(value)] = svgProp;
      return result;
    }, defaultProps as FeatherIconProps);
  };

  return <InlineSVG src={featherIcon(iconName)} raw={true} />;
};
